import React, { useEffect } from 'react';
import styled from 'styled-components';

import { Modal, Button, Billboard } from '@companycam/slab-web';
import { translate } from 'translations/i18n';
import toast from 'helpers/toast';
import { useGetTrustyIndustries } from 'data/v3/trusty';
import {
  useEditPublicProfile,
  useGetPublicProfiles,
  useGetPublicProfileTags,
} from 'data/v3/publicProfiles';
import { useGetStates } from 'data/v1/regions/useGetStates';
import { useModal } from 'components/shared/hooks';
import Steven from 'helpers/steven';

import {
  Row,
  Loading,
  ModalHeader,
  ErrorMessageRow,
} from '../CommonComponents';
import { useProfileForm } from '../useProfileForm';
import { IndustriesField } from '../FormFields/IndustriesField';
import { ServicesField } from '../FormFields/ServicesField';
import { TextField } from '../FormFields/TextField';
import { StateField } from '../FormFields/StateField';

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--cc_size_spacing_m);
`;

const NonPrimaryState = ({ isLoading }: { isLoading: boolean }) => {
  if (isLoading) {
    return <Loading />;
  }

  return (
    <Billboard
      title={translate('core.errorBoundaryFallback.heading')}
      color="destroy"
      iconName="alert-circle"
      message={translate('core.errorBoundaryFallback.message')}
    />
  );
};

export type EditPublicProfileModalProps = {};

export const EditPublicProfileModal: React.FC<
  EditPublicProfileModalProps
> = () => {
  const { hideModal } = useModal();
  const publicProfiles = useGetPublicProfiles();

  // fetch this here so we can use one loading state for the entire modal
  const industriesResponse = useGetTrustyIndustries();
  const states = useGetStates('US');
  const formMethods = useProfileForm();

  const existingProfile = publicProfiles.data?.data[0];
  const tagsResponse = useGetPublicProfileTags({
    profileId: existingProfile?.id,
  });

  const editPublicProfile = useEditPublicProfile({
    profileId: existingProfile?.id,
    onError() {
      toast.error(translate('portfolio.profile.updateError'));
    },
    onSuccess(response) {
      toast.success(translate('portfolio.profile.updateSuccess'));
      hideModal();
      Steven.entityCrud({
        entity_type: 'public_profile',
        entity_id: response.data.id.toString(),
        action: 'updated',
      });
    },
  });

  const handleOnSave = formMethods.handleSubmit((data) => {
    editPublicProfile.mutate(data);
  });

  useEffect(() => {
    if (!publicProfiles.isFetching && existingProfile) {
      const sanitizedProfile = Object.fromEntries(
        Object.entries(existingProfile).filter(([, value]) => value !== null),
      );

      formMethods.reset({
        ...sanitizedProfile,
        serviceAreas: (tagsResponse.data?.data.serviceAreas || []).map(
          (area) => area.name,
        ),
        industries: (tagsResponse.data?.data.industries || []).map(
          (industry) => `${industry.id}`,
        ),
      });
    }
  }, [
    publicProfiles.isFetching,
    existingProfile,
    tagsResponse.isFetching,
    tagsResponse.data,
    formMethods,
  ]);

  const isLoading = [
    states,
    industriesResponse,
    publicProfiles,
    tagsResponse,
  ].some((response) => response.isLoading);
  const isError = [
    states,
    industriesResponse,
    publicProfiles,
    tagsResponse,
  ].some((response) => response.isError);
  const refetchAll = () => {
    states.refetch();
    industriesResponse.refetch();
    publicProfiles.refetch();
    tagsResponse.refetch();
  };

  const phoneOrEmailError =
    formMethods.formState.errors.phoneNumber?.message ||
    formMethods.formState.errors.email?.message;

  return (
    <Modal
      title={translate('portfolio.profile.editModalTitle')}
      isOpen
      onClose={hideModal}
      size="small"
      headerComponent={
        <ModalHeader
          headerTitle={translate('portfolio.profile.editModalTitle')}
        />
      }
      footerActions={
        <Button
          color="primary"
          onClick={isError ? refetchAll : handleOnSave}
          loading={editPublicProfile.isPending}
          disabled={
            isLoading ||
            editPublicProfile.isPending ||
            !formMethods.formState.isValid
          }
        >
          {isError
            ? translate('core.errorResetButton.message')
            : translate('common.save')}
        </Button>
      }
      footerContentBefore={
        <Button color="subtle" onClick={hideModal}>
          {translate('common.cancel')}
        </Button>
      }
      bodyStyle={{
        paddingTop: 'var(--cc_size_spacing_xl)',
        paddingBottom: 'var(--cc_size_spacing_xl)',
        paddingRight: 'var(--cc_size_spacing_xl)',
        paddingLeft: 'var(--cc_size_spacing_xl)',
        maxHeight: 600,
      }}
    >
      {isLoading || isError ? (
        <NonPrimaryState isLoading={isLoading} />
      ) : (
        <ContentContainer>
          <Row>
            <TextField
              name="name"
              formMethods={formMethods}
              label={translate('common.companyName')}
              placeholder={translate(
                'portfolio.profile.companyNamePlaceholder',
              )}
            />
          </Row>
          <Row>
            <TextField
              name="addressLine1"
              formMethods={formMethods}
              label={translate('portfolio.profile.address1')}
              placeholder={translate('portfolio.profile.address1Placeholder')}
            />
            <TextField
              name="addressLine2"
              formMethods={formMethods}
              label={translate('portfolio.profile.address2')}
              placeholder={translate('portfolio.profile.address2Placeholder')}
            />
          </Row>
          <Row>
            <TextField
              name="city"
              formMethods={formMethods}
              label={translate('projects.forms.form.city')}
              placeholder={translate('projects.forms.form.city')}
            />
            <StateField formMethods={formMethods} />

            <TextField
              name="postalCode"
              formMethods={formMethods}
              label={translate('projects.forms.form.postalCode')}
              placeholder={translate('projects.forms.form.postalCode')}
            />
          </Row>
          <div>
            <Row>
              <TextField
                name="phoneNumber"
                formMethods={formMethods}
                label={translate('common.phoneNumber')}
                placeholder=""
                onChange={() => {
                  formMethods.trigger('phoneNumber');
                  formMethods.trigger('email');
                }}
              />
              <TextField
                name="email"
                formMethods={formMethods}
                label={translate('common.emailAddress')}
                placeholder=""
                onChange={() => {
                  formMethods.trigger('phoneNumber');
                  formMethods.trigger('email');
                }}
              />
            </Row>
            <ErrorMessageRow errorMessage={phoneOrEmailError} />
          </div>
          <Row>
            <TextField
              name="website"
              formMethods={formMethods}
              label={translate('portfolio.profile.website')}
              placeholder=""
            />
          </Row>
          <ServicesField formMethods={formMethods} />
          <IndustriesField formMethods={formMethods} />
        </ContentContainer>
      )}
    </Modal>
  );
};
